import React, { useContext, useEffect, useState } from 'react';
import {
  IonTabs,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonLabel,
  IonLoading
} from '@ionic/react';
import {
  Route,
  Redirect,
  RouteComponentProps,
  useHistory,
  Switch
} from 'react-router-dom';

import './MainTabNavigator.css';

import Home from '../pages/Home';
import MyAppointments from '../pages/MyAppointments';
import Account from '../pages/Account';
import Faq from '../pages/Faq';
import News from '../pages/News';

import { homeOutline, closeOutline, documentOutline } from 'ionicons/icons';
import Register from '../pages/Register';
import ProtectedRoute from './ProtectedRoute';
import NewsSingle from '../pages/NewsSingle';
import RGPD from '../pages/RGPD';
import CGU from '../pages/CGU';
import { useDispatch } from 'react-redux';
import { resetAppointment } from '../store/actions/appointments';
import LaboratoryRepository from '../repositories/LaboratoryRepository';
import { Laboratory } from '../models';
import MyResults from '../pages/MyResults';
import { ThemeContext } from '../components/theme/ThemeProvider';
import HomeCovidCheck from '../pages/HomeCovidCheck';
import { unilabsHome } from '../const';

type MainTabNavigatorProps = RouteComponentProps<{
  laboratorySlug: string;
}>;

const MainTabNavigator: React.FC<MainTabNavigatorProps> = ({ match }) => {
  const theme = useContext(ThemeContext);

  const [laboratoryLoading, setLaboratoryLoading] = useState(
    !!match.params.laboratorySlug
  );
  const [laboratories, setLaboratories] = useState<Laboratory[]>();
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    if (match.params.laboratorySlug) {
      setLaboratoryLoading(true);
      LaboratoryRepository.findMatchBySlug(match.params.laboratorySlug).then(
        (laboratories) => {
          if (laboratories.length) {
            setLaboratories(laboratories);
            setLaboratoryLoading(false);
          } else {
            history.push('/');
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(resetAppointment());
  }, [dispatch, history]);

  function closeDesktopMenu() {
    document.body.classList.remove('menu-open');
  }

  if (laboratoryLoading) {
    return <IonLoading isOpen={laboratoryLoading} />;
  }

  const matchUrl = match.url.replace(/\/$/, '');

  return (
    <>
      <IonTabs>
        <IonRouterOutlet>
          <Switch>
            <ProtectedRoute
              path={`${matchUrl}/:tab(mon-compte)`}
              component={Account}
              exact
            />
            <ProtectedRoute
              path={`${matchUrl}/:tab(rendez-vous)`}
              component={MyAppointments}
              exact
            />
            <ProtectedRoute
              path={`${matchUrl}/:tab(resultats)`}
              component={MyResults}
              exact
            />
            <Route
              path={`${matchUrl}/:tab(resume)`}
              component={() => {
                if (theme.home.type === 'covidRegistration') {
                  return <HomeCovidCheck />;
                } else {
                  return (
                    <Home
                      laboratorySlug={match.params.laboratorySlug}
                      laboratories={laboratories}
                    />
                  );
                }
              }}
              exact
            />

            <Route
              path={`${matchUrl}/:tab(actualites)`}
              component={News}
              exact
            />
            <Route
              path={`${matchUrl}/:tab(actualites)/:id`}
              component={NewsSingle}
              exact
            />
            <Route path={`${matchUrl}/:tab(faq)`} component={Faq} exact />
            <Route
              path={`${matchUrl}/:tab(register)`}
              component={Register}
              exact
            />
            <Route
              path={`${matchUrl}/data-protection`}
              component={RGPD}
              exact
            />
            <Route path={`${matchUrl}/cgu`} component={CGU} exact />
            <Redirect exact from={matchUrl} to={`${matchUrl}/resume`} />
            <Redirect from="*" to="/dashboard" />
          </Switch>
        </IonRouterOutlet>
        <IonTabBar slot="bottom">
          <IonTabButton className="ion-custom-hide-md-down tab-bar__close">
            <div
              onClick={(e) => {
                e.preventDefault();
                closeDesktopMenu();
              }}
              className="tab-icon-close-event-listener"
            >
              <IonIcon icon={closeOutline}></IonIcon>
            </div>
          </IonTabButton>
          <IonTabButton tab="resume" href={unilabsHome} mode="ios">
            <div
              onClick={(e) => {
                window.location.href = unilabsHome;
                e.preventDefault();
                history.push('/');
              }}
              className="tab-icon-event-listener"
            >
              <div className="tab-icon-wrapper">
                <IonIcon icon={homeOutline} />
              </div>
              <IonLabel>Accueil</IonLabel>
            </div>
          </IonTabButton>
          <IonTabButton
            tab="mon-compte"
            href={`${matchUrl}/mon-compte`}
            mode="ios"
          >
            <div onClick={closeDesktopMenu} className="tab-icon-event-listener">
              <div className="tab-icon-wrapper">
                <IonIcon src={require('../assets/icons/account.svg')} />
              </div>
              <IonLabel>Mon Compte</IonLabel>
            </div>
          </IonTabButton>
          <IonTabButton
            tab="rendez-vous"
            href={`${matchUrl}/rendez-vous`}
            mode="ios"
          >
            <div onClick={closeDesktopMenu} className="tab-icon-event-listener">
              <div className="tab-icon-wrapper">
                <IonIcon
                  src={require('../assets/icons/calendar.svg')}
                ></IonIcon>
              </div>
              <IonLabel>Mes Créneaux</IonLabel>
            </div>
          </IonTabButton>
          {theme.home.type === 'covidRegistration' ? (
            <IonTabButton
              tab="resultats"
              href={`${matchUrl}/resultats`}
              mode="ios"
            >
              <div
                onClick={closeDesktopMenu}
                className="tab-icon-event-listener"
              >
                <div className="tab-icon-wrapper">
                  <IonIcon icon={documentOutline}></IonIcon>
                </div>
                <IonLabel>Mes Résultats</IonLabel>
              </div>
            </IonTabButton>
          ) : null}
        </IonTabBar>
      </IonTabs>
    </>
  );
};

export default MainTabNavigator;
